import * as React from "react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Calendar as CalendarIcon } from "lucide-react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const DatePicker = ({
  selected,
  onChange,
  className,
  maxDate,
  availableDates,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    if (e.key === "Escape") {
      setIsOpen(false);
      return;
    }

    // Only handle navigation if we have available dates
    if (!availableDates || availableDates.size === 0) return;

    const currentDateStr = format(selected || new Date(), "yyyy-MM-dd");
    const dates = Array.from(availableDates).sort();
    const currentIndex = dates.indexOf(currentDateStr);

    let newDate;
    switch (e.key) {
      case "ArrowLeft":
        if (currentIndex > 0) {
          const [year, month, day] = dates[currentIndex - 1]
            .split("-")
            .map(Number);
          newDate = new Date(year, month - 1, day);
        }
        break;
      case "ArrowRight":
        if (currentIndex < dates.length - 1) {
          const [year, month, day] = dates[currentIndex + 1]
            .split("-")
            .map(Number);
          newDate = new Date(year, month - 1, day);
        }
        break;
      case "ArrowUp":
        if (currentIndex >= 7) {
          const [year, month, day] = dates[currentIndex - 7]
            .split("-")
            .map(Number);
          newDate = new Date(year, month - 1, day);
        }
        break;
      case "ArrowDown":
        if (currentIndex < dates.length - 7) {
          const [year, month, day] = dates[currentIndex + 7]
            .split("-")
            .map(Number);
          newDate = new Date(year, month - 1, day);
        }
        break;
      case " ":
      case "Enter":
        e.preventDefault();
        setIsOpen(false);
        break;
      default:
        return;
    }

    if (newDate && onChange) {
      e.preventDefault();
      onChange(newDate);
    }
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={"outline"}
            className={cn(
              "w-full justify-start text-left font-normal px-3 py-2",
              !selected && "text-muted-foreground",
            )}
            onKeyDown={handleKeyDown}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {selected ? format(selected, "PPP") : <span>Pick a date</span>}
            <ChevronDown className="h-4 w-4 opacity-50 ml-auto" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(date) => {
              onChange(date);
              setIsOpen(false);
            }}
            maxDate={maxDate}
            availableDates={availableDates}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DatePicker;
